import {
    Box,
    Container,
    Button,
    Typography,
    Card,
    CircularProgress,
    TextField,
    Alert,
    Stack,
    InputAdornment,
    IconButton,
    Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRegister } from '../../react-query/auth-queries';
import { useUser } from '../../providers/user-provider';
import { useState } from 'react';
import { ROUTES } from '../../router/routes';
import { Visibility, VisibilityOff, Person, Business, Email, Lock } from '@mui/icons-material';
import { brand } from '../../shared-theme/themePrimitives';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

export default function SignupPage() {
    const navigate = useNavigate();
    const { setToken, setUser } = useUser();
    
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const { mutateAsync: register, isPending } = useRegister();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            const response = await register({ 
                firstName,
                lastName,
                companyName,
                email, 
                password 
            });
            
            if (response.success && response.data) {
                 const { accessToken, refreshToken, user } = response.data;
                 
                if (accessToken) {
                    setToken('accessToken', accessToken);
                    if (refreshToken) setToken('refreshToken', refreshToken);
                    if (user) setUser(user);
                    
                    // Explicit check for false to handle potential undefined cases safely
                    if (user && user.isEmailVerified === false) {
                        navigate(ROUTES.CHECK_EMAIL.path);
                    } else {
                        navigate(ROUTES.DASHBOARD.path);
                    }
                } else {
                    navigate(ROUTES.LOGIN.path);
                }
            } else {
                setError(response.message || 'Registration failed');
            }
        } catch (err: any) {
            console.error("Registration error:", err);
            setError(err.response?.data?.message || 'An error occurred during registration');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 50% -20%, rgba(210, 225, 255, 0.5) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: '#FAFAFA',
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="sm">
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <PodcastIcon size={48} color={brand[500]} />
                    </Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={800} sx={{ color: '#0F172A', fontSize: { xs: '1.75rem', md: '2rem' } }}>
                        Create your account
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                        Start your 14-day free trial. No credit card required.
                    </Typography>
                </Box>

                <Card 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 3, sm: 5 }, 
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                    }}
                >
                     {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                            {error}
                        </Alert>
                     )}

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                        First Name
                                    </Typography>
                                    <TextField
                                        autoComplete="given-name"
                                        name="firstName"
                                        required
                                        fullWidth
                                        id="firstName"
                                        placeholder="John"
                                        autoFocus
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person sx={{ color: 'text.disabled', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                        Last Name
                                    </Typography>
                                    <TextField
                                        required
                                        fullWidth
                                        id="lastName"
                                        name="lastName"
                                        placeholder="Doe"
                                        autoComplete="family-name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                    />
                                </Box>
                            </Stack>

                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    Company Name
                                </Typography>
                                <TextField
                                    fullWidth
                                    id="companyName"
                                    name="companyName"
                                    placeholder="Acme Inc."
                                    autoComplete="organization"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Business sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    Work Email
                                </Typography>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    name="email"
                                    placeholder="john@company.com"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    Password
                                </Typography>
                                <TextField
                                    required
                                    fullWidth
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    helperText="At least 8 characters"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isPending}
                                sx={{ 
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    bgcolor: brand[400],
                                    '&:hover': { bgcolor: brand[500] },
                                    boxShadow: '0 10px 20px -10px rgba(225, 89, 27, 0.4)'
                                }}
                            >
                                {isPending ? <CircularProgress size={24} color="inherit" /> : 'Get Started'}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Already have an account?{' '}
                            <MuiLink 
                                component="button"
                                onClick={() => navigate(ROUTES.LOGIN.path)}
                                sx={{ 
                                    fontWeight: 600, 
                                    textDecoration: 'none',
                                    color: brand[500] 
                                }}
                            >
                                Sign in
                            </MuiLink>
                        </Typography>
                    </Box>
                </Card>
                
                <Typography variant="caption" display="block" align="center" color="text.disabled" sx={{ mt: 4 }}>
                    By signing up, you agree to our Terms of Service and Privacy Policy.
                </Typography>
            </Container>
        </Box>
    );
}
